import { FC } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, Card, CardContent, Stack, Chip, Skeleton, Link } from '@mui/material';
import {
  CampaignRounded as CampaignIcon,
  AttachMoneyRounded,
  CalendarTodayRounded,
  TrendingUpRounded,
  WorkRounded,
  AdsClickRounded,
} from '@mui/icons-material';

import { CampaignPPCValue } from '@libs/types';
import { useCampaignViewQuery } from '@carcareers/store';
import {
  SkeletonForm,
  CampaignUpdateForm,
  DashboardPageContainer,
  CampaignStatusChip,
  CampaignPartnerChip,
} from '@carcareers/components';
import { FormProvider } from '@carcareers/contexts';
import { routes, useIsMobile } from '@carcareers/utils';

export const CampaignView: FC = () => {
  const { id } = useParams();
  const campaignId = id ? parseInt(id) : undefined;
  const isMobile = useIsMobile();

  const { data: campaign, isLoading } = useCampaignViewQuery(campaignId ?? 0, { skip: !campaignId });

  const formatCurrency = (amount?: number) => {
    return amount ? `$${amount.toLocaleString()}` : '$0';
  };

  const formatDate = (date?: string | Date) => {
    return date ? new Date(date).toLocaleDateString() : 'Not set';
  };

  return (
    <DashboardPageContainer idReplacement={`${campaign?.post?.job?.title} Campaign`}>
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          backgroundColor: 'background.paper',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Stack direction={isMobile ? 'column' : 'row'} spacing={3} alignItems={isMobile ? 'flex-start' : 'center'}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CampaignIcon sx={{ color: 'primary.contrastText', fontSize: 40 }} />
            </Box>
            <Stack spacing={1} sx={{ flex: 1 }}>
              <Stack direction="row" sx={{ mt: 1, flexWrap: 'wrap' }}>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {isLoading ? (
                    <Skeleton variant="text" width={300} height={32} />
                  ) : (
                    <Link
                      href={`${routes.ADMIN_HOME}/${routes.ADMIN_POSTS_VIEW.replace(
                        ':id',
                        campaign?.post?.id?.toString() ?? '',
                      )}`}
                      sx={{
                        textDecoration: 'none',
                        color: 'primary.main',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      {campaign?.post?.job?.title || 'Unknown Job'} Campaign
                    </Link>
                  )}
                  {isLoading ? (
                    <Skeleton variant="rounded" width={120} height={32} sx={{ ml: 1 }} />
                  ) : (
                    <CampaignStatusChip status={campaign?.status} sx={{ ml: 1 }} />
                  )}
                </Typography>
              </Stack>
              <Typography variant="body1" color="text.secondary">
                {isLoading ? (
                  <Skeleton variant="text" width={200} height={20} />
                ) : (
                  `Campaign ID: ${campaign?.id || '...'}`
                )}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                {isLoading ? (
                  <Skeleton variant="rounded" width={120} height={32} />
                ) : (
                  <CampaignPartnerChip partner={campaign?.partner} />
                )}
                {isLoading ? (
                  <Skeleton variant="rounded" width={200} height={32} />
                ) : (
                  <Chip
                    icon={<CalendarTodayRounded />}
                    label={`Created ${formatDate(campaign?.createdAt)}`}
                    variant="outlined"
                    sx={{ px: 1 }}
                  />
                )}
              </Stack>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
      <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
        <Card
          elevation={0}
          sx={{
            flex: 1,
            borderRadius: 3,
            backgroundColor: 'background.paper',
            height: '100%',
          }}
        >
          <CardContent sx={{ p: 3, textAlign: 'center' }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                backgroundColor: 'error.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <AdsClickRounded sx={{ color: 'error.contrastText', fontSize: 28 }} />
            </Box>
            <Typography variant="h3" sx={{ fontWeight: 700, color: 'error.main', mb: 1 }}>
              {isLoading ? (
                <Skeleton variant="text" sx={{ width: 40, height: 32, mx: 'auto' }} />
              ) : (
                campaign?.post?.applications?.length || 0
              )}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              Applications
            </Typography>
          </CardContent>
        </Card>
        <Card
          elevation={0}
          sx={{
            flex: 1,
            borderRadius: 3,
            backgroundColor: 'background.paper',
            height: '100%',
          }}
        >
          <CardContent sx={{ p: 3, textAlign: 'center' }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                backgroundColor: 'success.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <AttachMoneyRounded sx={{ color: 'success.contrastText', fontSize: 28 }} />
            </Box>
            <Typography variant="h3" sx={{ fontWeight: 700, color: 'success.main', mb: 1 }}>
              {isLoading ? (
                <Skeleton variant="text" sx={{ width: 40, height: 32, mx: 'auto' }} />
              ) : (
                formatCurrency(campaign?.budget)
              )}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              Budget
            </Typography>
          </CardContent>
        </Card>
        <Card
          elevation={0}
          sx={{
            flex: 1,
            borderRadius: 3,
            backgroundColor: 'background.paper',
            height: '100%',
          }}
        >
          <CardContent sx={{ p: 3, textAlign: 'center' }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                backgroundColor: 'warning.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <TrendingUpRounded sx={{ color: 'warning.contrastText', fontSize: 28 }} />
            </Box>
            <Typography variant="h3" sx={{ fontWeight: 700, color: 'warning.main', mb: 1 }}>
              {isLoading ? (
                <Skeleton variant="text" sx={{ width: 40, height: 32, mx: 'auto' }} />
              ) : (
                (() => {
                  if (!campaign?.startDate) return 'N/A';
                  const start = new Date(campaign.startDate);
                  const now = new Date();
                  const days = Math.floor((now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
                  return Math.max(0, days);
                })()
              )}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              Days Running
            </Typography>
          </CardContent>
        </Card>
        <Card
          elevation={0}
          sx={{
            flex: 1,
            borderRadius: 3,
            backgroundColor: 'background.paper',
            height: '100%',
          }}
        >
          <CardContent sx={{ p: 3, textAlign: 'center' }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                backgroundColor: 'info.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <WorkRounded sx={{ color: 'info.contrastText', fontSize: 28 }} />
            </Box>
            <Stack
              direction="row"
              spacing={1}
              sx={{ mt: 1, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center' }}
            >
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'info.main', mb: 1 }}>
                {isLoading ? (
                  <Skeleton variant="text" sx={{ width: 40, height: 32, mx: 'auto' }} />
                ) : (
                  campaign?.ppc || 'N/A'
                )}
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'info.main', mb: 1 }}>
                {isLoading ? (
                  <Skeleton variant="text" sx={{ width: 40, height: 32, mx: 'auto' }} />
                ) : (
                  `(${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
                    CampaignPPCValue[campaign?.ppc?.toUpperCase() || ''],
                  )})`
                )}
              </Typography>
            </Stack>
            <Typography variant="subtitle2" color="text.secondary">
              PPC Model
            </Typography>
          </CardContent>
        </Card>
      </Stack>
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          backgroundColor: 'background.paper',
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h4" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CampaignIcon sx={{ color: 'primary.main' }} />
              Campaign Details
            </Typography>
          </Box>
          <Box
            sx={{
              p: 3,
              '& form': {
                height: '100%',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              },
            }}
          >
            <FormProvider initialData={campaign}>
              {isLoading ? <SkeletonForm /> : <CampaignUpdateForm disablePartner />}
            </FormProvider>
          </Box>
        </CardContent>
      </Card>
    </DashboardPageContainer>
  );
};

export default CampaignView;
